import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ScrollText } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useProfile } from "@/context/ProfileContext";
import { useLanguage } from "@/i18n/LanguageContext";

/**
 * Phase 10 (Google Play UGC compliance): explicit, one-time acceptance of the
 * community Terms before the first share-to-community. Acceptance is stored
 * server-side (profiles.ugc_terms_accepted_at) via ProfileContext.
 */
export const UgcTermsDialog = ({ open, onOpenChange, onAccepted }) => {
  const { acceptUgcTerms } = useProfile();
  const { t } = useLanguage();
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleOpenChange = (o) => {
    if (!o) setChecked(false);
    onOpenChange(o);
  };

  const handleAccept = async () => {
    setSaving(true);
    try {
      await acceptUgcTerms();
      toast.success(t("ugcTerms.success"));
      setChecked(false);
      onOpenChange(false);
      onAccepted?.();
    } catch {
      toast.error(t("ugcTerms.error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="ugc-terms-dialog"
        className="max-w-md bg-slate-900/95 backdrop-blur-xl border-white/10 text-white"
      >
        <DialogHeader>
          <div className="mx-auto h-12 w-12 rounded-2xl bg-cyan-300/10 border border-cyan-300/25 flex items-center justify-center">
            <ScrollText size={22} className="text-cyan-300/80" />
          </div>
          <DialogTitle className="text-center">{t("ugcTerms.title")}</DialogTitle>
          <DialogDescription className="text-center text-[hsl(var(--muted-foreground))]">
            {t("ugcTerms.desc")}
          </DialogDescription>
        </DialogHeader>

        <p className="text-xs leading-relaxed text-white/70">{t("ugcTerms.rules")}</p>

        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
          <Link
            data-testid="ugc-terms-link"
            to="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-cyan-300 underline underline-offset-4 hover:text-cyan-200"
          >
            {t("ugcTerms.termsLink")}
          </Link>
          <Link
            data-testid="ugc-privacy-link"
            to="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-cyan-300 underline underline-offset-4 hover:text-cyan-200"
          >
            {t("ugcTerms.privacyLink")}
          </Link>
        </div>

        <div className="flex items-start gap-2.5 rounded-xl bg-white/5 border border-white/10 p-3">
          <Checkbox
            id="ugc-terms-checkbox"
            data-testid="ugc-terms-checkbox"
            checked={checked}
            onCheckedChange={(v) => setChecked(v === true)}
            className="mt-0.5 border-white/30 data-[state=checked]:bg-cyan-300 data-[state=checked]:text-slate-950 data-[state=checked]:border-cyan-300"
          />
          <Label
            htmlFor="ugc-terms-checkbox"
            className="text-sm leading-relaxed text-white/85 cursor-pointer"
          >
            {t("ugcTerms.checkboxLabel")}
          </Label>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            data-testid="ugc-terms-cancel-button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            className="rounded-xl bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white"
          >
            {t("ugcTerms.cancel")}
          </Button>
          <Button
            data-testid="ugc-terms-accept-button"
            onClick={handleAccept}
            disabled={!checked || saving}
            className="rounded-xl bg-cyan-300 text-slate-950 font-semibold hover:bg-cyan-200 active:scale-[0.98] disabled:opacity-50"
          >
            {saving ? <Loader2 className="animate-spin" size={16} /> : t("ugcTerms.accept")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
